import axios from 'axios';

// API URL configuration for different environments
const getApiBaseUrl = () => {
  // Always use REACT_APP_API_URL if set (for both dev and prod)
  if (process.env.REACT_APP_API_URL) {
    console.log('🔗 Using configured API URL:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }
  
  // Development fallback
  const devUrl = 'http://localhost:8001';
  console.log('🔧 Development fallback: Using API URL', devUrl);
  return devUrl;
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Browser UUID for anonymous usage tracking (persists in localStorage)
function getBrowserId(): string {
  const key = 'salesai-browser-id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = (Math.random() * 16) | 0;
          return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
    localStorage.setItem(key, id);
  }
  return id;
}

// Request interceptor to add auth header and browser ID
api.interceptors.request.use(
  (config) => {
    const apiKey = localStorage.getItem('salesai-api-key');
    if (apiKey) {
      config.headers.Authorization = `Bearer ${apiKey}`;
    }
    config.headers['X-Browser-Id'] = getBrowserId();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 errors (unauthorized) or network errors that might be auth-related
    if (error.response?.status === 401 || 
        (error.code === 'ERR_NETWORK' && localStorage.getItem('salesai-api-key'))) {
      // Store error message for display after redirect
      localStorage.setItem('salesai-auth-error', 'Invalid Access Key. Please check and try again.');
      // Clear invalid Access Key
      localStorage.removeItem('salesai-api-key');
      // Instead of reloading, dispatch a custom event to trigger auth state update
      window.dispatchEvent(new CustomEvent('auth-error', { 
        detail: { message: 'Invalid Access Key. Please check and try again.' } 
      }));
      return Promise.reject(new Error('Authentication required'));
    }

    // Only log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', error.message, error.response?.status);
    }
    return Promise.reject(error);
  }
);

// Types for API responses





export interface DealDetails {
  sales_cycle_id: string;
  sales_cycle_name: string;
  status: string;
  deal_score: number;
  cycle_owner_name: string;
  cycle_owner_team: string;
  company_name: string;
  number_of_events: number;
  number_of_meetings_held: number;
  raw_created_at: string;
  raw_updated_at: string;
  deal_scores?: any;
  events?: any[];
}


export interface SalesStageScoring {
  stage_name: string;
  icon: string;
  active_deals: number;
  total_amount?: number; // Optional since API doesn't return this
  scored_deals?: number; // Count of deals with scoring data
  scored_deals_amount?: number; // Amount for deals with scoring data
  avg_score: number | null;
  max_score: number;
  deals_with_exit_criteria_move: number;
  deals_with_exit_criteria_review: number;
  deals_with_attention: number;
  deals_with_no_recommended_type?: number; // New field for open cycles with no recommended type
  amount_all_criteria: number;
  amount_one_or_no_criteria: number; // Actual API field name
  qualified?: number; // New field for open cycles qualification
  unqualified?: number; // New field for open cycles qualification
  cycles_with_transcripts?: number; // New field for cycles with transcripts
  exit_criteria: string[];
  key_criteria: string[];
}

export interface DealAttention {
  sales_cycle_id: string;
  sales_cycle_name: string;
  company_name: string;
  cycle_owner_name: string;
  cycle_owner_team: string;
  status: string;
  deal_score: number | null;
  number_of_events: number | null;
  number_of_meetings_held: number | null;
  raw_created_at: string;
  raw_updated_at: string;
  days_in_stage: number;
  current_stage_score: number;
  current_stage: string;
  exit_criteria_met?: number;
  total_criteria_in_stage?: number;
  deal_stage_label?: string;
  deal_amount?: number;
  pipeline_label?: string;
  deal_name?: string;
  deal_id?: string;
  qualification_status?: string | null;
  qualification_stage?: string | null;
  has_meeting_content?: boolean;
}



// API methods
export const salesCyclesApi = {

  getStagesScoring: async (cycleType?: string, accountValue?: string, salesRep?: string, team?: string, meetingDispositions?: string): Promise<SalesStageScoring[]> => {
    const params = new URLSearchParams();
    if (cycleType) {
      params.append('cycle_type', cycleType);
    }
    if (accountValue) {
      params.append('account_value', accountValue);
    }
    if (salesRep) {
      params.append('sales_rep', salesRep);
    }
    if (team) {
      params.append('team', team);
    }
    if (meetingDispositions) {
      params.append('meeting_dispositions', meetingDispositions);
    }
    const queryString = params.toString();
    const url = `/api/v1/deals/stages-scoring${queryString ? `?${queryString}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  getDealsNeedingAttention: async (stageName: string): Promise<DealAttention[]> => {
    const response = await api.get(`/api/v1/deals/stage/${encodeURIComponent(stageName)}?category=attention`);
    return response.data;
  },

  getDealsByStageCategory: async (stageName: string, category: string): Promise<DealAttention[]> => {
    const response = await api.get(`/api/v1/deals/stage/${encodeURIComponent(stageName)}?category=${encodeURIComponent(category)}`);
    return response.data;
  },

  // New unified method for more flexible filtering
  getDealsByStage: async (
    stageName: string, 
    filters?: {
      category?: string;
      minCriteria?: number;
      maxCriteria?: number;
      minScore?: number;
      maxScore?: number;
      page?: number;
      pageSize?: number;
      cycleType?: string | null;
      accountValue?: string | null;
      salesRep?: string | null;
      team?: string | null;
    }
  ): Promise<DealAttention[]> => {
    const params = new URLSearchParams();
    
    if (filters?.category) params.append('category', filters.category);
    if (filters?.minCriteria !== undefined) params.append('min_criteria', filters.minCriteria.toString());
    if (filters?.maxCriteria !== undefined) params.append('max_criteria', filters.maxCriteria.toString());
    if (filters?.minScore !== undefined) params.append('min_score', filters.minScore.toString());
    if (filters?.maxScore !== undefined) params.append('max_score', filters.maxScore.toString());
    if (filters?.page !== undefined) params.append('page', filters.page.toString());
    if (filters?.pageSize !== undefined) params.append('page_size', filters.pageSize.toString());
    if (filters?.cycleType) params.append('cycle_type', filters.cycleType);
    if (filters?.accountValue) params.append('account_value', filters.accountValue);
    if (filters?.salesRep) params.append('sales_rep', filters.salesRep);
    if (filters?.team) params.append('team', filters.team);
    
    const queryString = params.toString();
    const url = `/api/v1/deals/stage/${encodeURIComponent(stageName)}${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response.data;
  },



  getDealDetails: async (salesCycleId: string): Promise<DealDetails> => {
    const response = await api.get(`/api/v1/deals/${salesCycleId}`);
    return response.data;
  },






  getUnqualifiedCyclesBreakdown: async (
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{investigate_count: number, forget_count: number}> => {
    const params = new URLSearchParams();
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);

    const queryString = params.toString();
    const response = await api.get(`/api/v1/deals/unqualified-cycles-breakdown?${queryString}`);
    return response.data;
  },

  getQualifiedCyclesBreakdown: async (
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    enterprise_open_deal_count: number,
    enterprise_investigate_count: number,
    transactional_open_deal_count: number,
    transactional_investigate_count: number
  }> => {
    const params = new URLSearchParams();
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);

    const queryString = params.toString();
    const response = await api.get(`/api/v1/deals/qualified-cycles-breakdown?${queryString}`);
    return response.data;
  },

  getOpenCyclesQualificationBreakdown: async (
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null,
    meetingDispositions?: string[]
  ): Promise<{
    transactional_deal_count: number,
    transactional_zero_count: number,
    enterprise_count: number,
    nurture_count: number,
    conflict_count: number,
    bad_fit_count: number,
    trash_count: number
  }> => {
    const params = new URLSearchParams();
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    if (meetingDispositions && meetingDispositions.length > 0) {
      params.append('meeting_dispositions', meetingDispositions.join(','));
    }

    const queryString = params.toString();
    const response = await api.get(`/api/v1/deals/open-cycles-qualification-breakdown?${queryString}`);
    return response.data;
  },

  getSalesReps: async (team?: string): Promise<{hs_owner_id: string, rep_name: string, team_name: string}[]> => {
    const url = team 
      ? `/api/v1/deals/filters/sales-reps?team=${encodeURIComponent(team)}`
      : '/api/v1/deals/filters/sales-reps';
    const response = await api.get(url);
    return response.data;
  },

  getTeams: async (): Promise<{team_name: string, rep_count: number}[]> => {
    const response = await api.get('/api/v1/deals/filters/teams');
    return response.data;
  },

  getCompanies: async (search?: string): Promise<{company_name: string, company_id: string}[]> => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await api.get(`/api/v1/deals/filters/companies${params}`);
    return response.data;
  },

  getDealScoringTrends: async (
    period: 'week' | 'month' | 'quarter' = 'month',
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    period_start: string;
    period_label: string;
    period_type: string;
    total_deals: number;
    scored_deals: number;
    avg_score: number;
    total_amount: number;
  }[]> => {
    const params = new URLSearchParams();
    params.append('period', period);
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/deals/trends/scoring?${queryString}`);
    return response.data;
  },

  getStageTrendsTotalScore: async (
    period: 'week' | 'month' | 'quarter' = 'month',
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    period_start: string;
    period_label: string;
    period_type: string;
    stage_name: string;
    stage_order: number;
    deals_in_stage: number;
    scored_deals: number;
    avg_score: number;
    total_amount: number;
    total_stage_entries: number;
  }[]> => {
    const params = new URLSearchParams();
    params.append('period', period);
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/analytics/trends/stage-total-score?${queryString}`);
    return response.data;
  },

  getDealStageTrends: async (
    period: 'week' | 'month' | 'quarter' = 'month',
    months: number = 12,
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    period_start: string;
    period_label: string;
    period_type: string;
    stage_name: string;
    stage_order: number;
    deals_in_stage: number;
    scored_deals: number;
    avg_score: number;
    total_amount: number;
    total_stage_entries: number;
  }[]> => {
    const params = new URLSearchParams();
    params.append('period', period);
    params.append('months', months.toString());
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);

    const queryString = params.toString();
    const response = await api.get(`/api/v1/deals/trends/stages?${queryString}`);
    return response.data;
  },

  getOpenCyclesTrends: async (
    period: 'week' | 'month' | 'quarter' = 'week',
    weeks: number = 12,
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null,
    meetingDispositions?: string[] | null,
    qualificationType?: 'DEAL' | 'ZERO' | null
  ): Promise<{
    period_date: string;
    period_label: string;
    newly_originated: number;
    newly_originated_no_stage_change: number;
    newly_originated_with_stage_change: number;
    old_with_touchbase: number;
    old_without_touchbase: number;
    total_active_cycles: number;
  }[]> => {
    const params = new URLSearchParams();
    params.append('period', period);
    params.append('weeks', weeks.toString());
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    if (meetingDispositions && meetingDispositions.length > 0) {
      params.append('meeting_dispositions', meetingDispositions.join(','));
    }
    if (qualificationType) {
      params.append('qualification_type', qualificationType);
    }

    const queryString = params.toString();
    const response = await api.get(`/api/v1/deals/open-cycles/trends?${queryString}`);
    return response.data;
  },

  // Dashboard qualification breakdown (qualified/unqualified with qualification_type and action_type)
  getOpenCyclesByCategory: async (
    category: string,
    page: number = 1,
    pageSize: number = 20,
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null,
    qualificationType?: string | null,
    actionType?: string | null,
    meetingDispositions?: string[]
  ): Promise<any> => {
    const params = new URLSearchParams();
    params.append('category', encodeURIComponent(category));
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    if (qualificationType) params.append('qualification_type', qualificationType);
    if (actionType) params.append('action_type', actionType);
    if (meetingDispositions && meetingDispositions.length > 0) {
      params.append('meeting_dispositions', meetingDispositions.join(','));
    }

    const queryString = params.toString();
    const response = await api.get(`/api/v1/deals/open-cycles-by-category?${queryString}`);
    return response.data;
  },

  // Meeting Activity page by activity category (newly_originated, old_with_touchbase, old_without_touchbase, all)
  getOpenCyclesByActivity: async (
    category: 'newly_originated' | 'old_with_touchbase' | 'old_without_touchbase' | 'all',
    page: number = 1,
    pageSize: number = 20,
    periodDate?: string | null,
    period?: 'week' | 'month' | 'quarter',
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null,
    sortField?: string,
    sortDirection?: 'asc' | 'desc',
    meetingDispositions?: string[],
    qualificationType?: 'DEAL' | 'ZERO' | null
  ): Promise<{
    data: Array<{
      sales_cycle_id: number;
      company_name: string;
      cycle_owner_name: string;
      start_date: string;
      number_of_meetings_held: number;
      number_of_events: number;
      recommended_deal_qualification: string | null;
      recommended_deal_qualification_type: string | null;
      account_tier: string | null;
      cycle_type: string | null;
      has_analysis_record: boolean;
    }>;
    pagination: {
      current_page: number;
      page_size: number;
      total_deals: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  }> => {
    const params = new URLSearchParams();
    params.append('category', category);
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    if (periodDate) params.append('period_date', periodDate);
    if (period) params.append('period', period);
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    if (sortField) params.append('sort_field', sortField);
    if (sortDirection) params.append('sort_direction', sortDirection);
    if (meetingDispositions && meetingDispositions.length > 0) {
      params.append('meeting_dispositions', meetingDispositions.join(','));
    }
    if (qualificationType) {
      params.append('qualification_type', qualificationType);
    }

    const queryString = params.toString();
    const response = await api.get(`/api/v1/deals/open-cycles/by-activity?${queryString}`);
    return response.data;
  },

  getDecisionMakersTrends: async (
    period: 'week' | 'month' | 'quarter' = 'month',
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    period_start: string;
    period_label: string;
    period_type: string;
    total_deals: number;
    deals_with_champions: number;
    champion_percentage: number;
    total_amount: number;
    amount_with_champions: number;
  }[]> => {
    const params = new URLSearchParams();
    params.append('period', period);
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);

    const queryString = params.toString();
    const response = await api.get(`/api/v1/deals/trends/decision-makers?${queryString}`);
    return response.data;
  },

  getDecisionMakersTrendsMMPlus: async (
    period: 'week' | 'month' | 'quarter' = 'month',
    cycleType?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    period_start: string;
    period_label: string;
    period_type: string;
    total_deals: number;
    deals_with_champions: number;
    champion_percentage: number;
    total_amount: number;
    amount_with_champions: number;
  }[]> => {
    const params = new URLSearchParams();
    params.append('period', period);
    if (cycleType) params.append('cycle_type', cycleType);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);

    const queryString = params.toString();
    const response = await api.get(`/api/v1/deals/trends/decision-makers-mm-plus?${queryString}`);
    return response.data;
  },
};

// Insights API
export const insightsApi = {

  getTacticalInsights: async (
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    tactical_insights: {
      display_name: string;
      criteria_name: string;
      stage_name: string;
      amount_with_criteria: number;
      amount_without_criteria: number;
      winrate_with: number;
      winrate_without: number;
      deals_with_criteria: number;
      deals_without_criteria: number;
      closed_deals_with_criteria: number;
      closed_deals_without_criteria: number;
      amount_with_criteria_deals: number;
      amount_without_criteria_deals: number;
      // NEW: Open deals without criteria
      open_deals_without_criteria: number;
      open_amount_without_criteria: number;
    }[];
    summary: {
      total_criteria: number;
      total_deals_analyzed: number;
    };
  }> => {
    const params = new URLSearchParams();
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/insights/tactical-insights${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  getStrategicInsights: async (
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    strategic_insights: {
      primary_reason: string;
      deal_count: number;
      total_amount: number;
      avg_deal_size: number;
      avg_days_to_close: number;
      percentage_of_deals: number;
      percentage_of_amount: number;
    }[];
    total_deals: number;
    categorized_deals: number;
    summary: {
      total_deals: number;
      total_amount: number;
      top_3_reasons: string[];
    };
  }> => {
    const params = new URLSearchParams();
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/insights/strategic-insights${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  getClosedLostReasonsAnalysis: async (
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    total_deals: number;
    categorized_deals: number;
    buckets: {
      [key: string]: {
        count: number;
        percentage: number;
        description: string;
        suggested_actions: string[];
        deals: {
          sales_cycle_id: string;
          company_name: string;
          cycle_owner_name: string;
          cycle_owner_team: string;
          status: string;
          cycle_type: string;
          total_deal_amount: number;
          days_to_close: number;
          how_closed_lost: string;
          why_closed_lost_primary: string;
          why_closed_lost_additional: string;
          loss_category: string;
        }[];
      };
    };
    raw_reasons: any[];
    analysis: string;
  }> => {
    const params = new URLSearchParams();
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/analytics/closed-lost-reasons${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  getClosedLostDealsByBucket: async (
    bucketName: string,
    page: number = 1,
    pageSize: number = 20,
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    bucket_name: string;
    bucket_description: string;
    deals: {
      sales_cycle_id: string;
      company_name: string;
      cycle_owner_name: string;
      cycle_owner_team: string;
      status: string;
      cycle_type: string;
      account_value: string;
      deal_score: number;
      start_date: string;
      closed_date: string;
      days_to_close: number;
      total_deal_amount: number;
      reason: string;
      how_closed_lost: string;
      why_closed_lost_primary: string;
      why_closed_lost_additional: string;
    }[];
    pagination: {
      page: number;
      page_size: number;
      total_deals: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  }> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/analytics/closed-lost-reasons/${encodeURIComponent(bucketName)}?${queryString}`);
    return response.data;
  },

  getTacticalInsightsDeals: async (
    criteria: string,
    page: number = 1,
    pageSize: number = 20,
    cycleType?: string | null,
    accountValue?: string | null,
    salesRep?: string | null,
    team?: string | null
  ): Promise<{
    criteria_name: string;
    criteria_display_name: string;
    deals: {
      sales_cycle_id: string;
      company_name: string;
      cycle_owner_name: string;
      cycle_owner_team: string;
      status: string;
      cycle_type: string;
      account_value: string;
      deal_score: number;
      start_date: string;
      closed_date: string;
      days_to_close: number;
      total_deal_amount: number;
      criteria_met: boolean;
      win_rate: number;
    }[];
    pagination: {
      current_page: number;
      page_size: number;
      total_deals: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    };
  }> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    if (cycleType) params.append('cycle_type', cycleType);
    if (accountValue) params.append('account_value', accountValue);
    if (salesRep) params.append('sales_rep', salesRep);
    if (team) params.append('team', team);
    
    const queryString = params.toString();
    const response = await api.get(`/api/v1/insights/tactical-insights/${encodeURIComponent(criteria)}/deals?${queryString}`);
    return response.data;
  },
};

// Vector Database Management API (MCP only)
export const vectorManagementApi = {
  getStatus: () => api.get('/api/v1/vector/status'),
  testSearch: () => api.get('/api/v1/vector/test'),
};

export default api; 